/**
 * iter1206 — Translate Stripe requirement codes into human-readable labels
 * for the Onboarding checklist UI.
 *
 * Stripe's `requirements.currently_due` / `past_due` arrays contain codes
 * like:
 *   • "company.verification.document"
 *   • "person_1U204OFZ2NiNbp7GYYogOIfy.address.line1"
 *   • "person_XXX.dob.day", "person_XXX.dob.month", "person_XXX.dob.year"
 *   • "external_account", "tos_acceptance.date"
 *
 * The user has no way to interpret these. This module:
 *   1. Groups related fields (e.g. dob.day + dob.month + dob.year → ONE row).
 *   2. Translates each group to a human label + short "what Stripe wants".
 *   3. Returns a stable list the UI can render as a checklist.
 */

const _RULES = [
  // ── Company-level ─────────────────────────────────────────────────
  {
    match: /^business_profile\.(url|mcc|product_description)$/,
    label: 'Business profile',
    hint: 'Website, business type or a short description of what you sell',
  },
  {
    match: /^company\.tax_id$/,
    label: 'Business tax ID',
    hint: 'Your company\'s tax / EIN / VAT number',
  },
  {
    match: /^company\.address\./,
    label: 'Business address',
    hint: 'Full registered business address',
    group: true,
  },
  {
    match: /^company\.name$/,
    label: 'Business legal name',
  },
  {
    match: /^company\.phone$/,
    label: 'Business phone number',
  },
  {
    match: /^company\.verification(\.document)?$/,
    label: 'Business verification document',
    hint: 'Upload a copy of your business registration (e.g. Certificate of Incorporation)',
  },
  {
    match: /^company\.directors_provided$/,
    label: 'Directors confirmation',
    hint: 'Confirm the list of directors on your account',
  },
  {
    match: /^company\.executives_provided$/,
    label: 'Executives confirmation',
    hint: 'Confirm the list of executives on your account',
  },
  {
    match: /^company\.owners_provided$/,
    label: 'Owners confirmation',
    hint: 'Confirm the list of beneficial owners',
  },

  // ── Individual / person-level ─────────────────────────────────────
  {
    match: /^person_[^.]+\.(first_name|last_name)$/,
    label: 'Owner\'s legal name',
    hint: 'First and last name as it appears on government ID',
    group: true,
  },
  {
    match: /^person_[^.]+\.dob\./,
    label: 'Owner\'s date of birth',
    hint: 'Day, month and year',
    group: true,
  },
  {
    match: /^person_[^.]+\.address\./,
    label: 'Owner\'s residential address',
    hint: 'Street, city and postal code (not a business address)',
    group: true,
  },
  {
    match: /^person_[^.]+\.phone$/,
    label: 'Owner\'s phone number',
  },
  {
    match: /^person_[^.]+\.email$/,
    label: 'Owner\'s email address',
  },
  {
    match: /^person_[^.]+\.id_number$/,
    label: 'Owner\'s personal ID number',
    hint: 'Government ID number (e.g. SSN last 4 in US, NI number in UK)',
  },
  {
    match: /^person_[^.]+\.ssn_last_4$/,
    label: 'Owner\'s SSN (last 4 digits)',
  },
  {
    match: /^person_[^.]+\.verification(\.document|\.additional_document)?$/,
    label: 'Owner\'s identity verification',
    hint: 'Upload a photo of a government-issued ID (passport, driving licence)',
    group: true,
  },
  {
    match: /^person_[^.]+\.political_exposure$/,
    label: 'Politically exposed person disclosure',
  },
  {
    match: /^person_[^.]+\.relationship\./,
    label: 'Owner\'s relationship to the business',
    hint: 'Role, ownership %, and whether they represent the business',
    group: true,
  },
  {
    match: /^person_[^.]+/,
    label: 'Owner information',
    hint: 'Additional owner or representative details',
    group: true,
  },

  // ── Individual-account variant ───────────────────────────────────
  {
    match: /^individual\.(first_name|last_name)$/,
    label: 'Your legal name',
    group: true,
  },
  {
    match: /^individual\.dob\./,
    label: 'Your date of birth',
    group: true,
  },
  {
    match: /^individual\.address\./,
    label: 'Your residential address',
    group: true,
  },
  {
    match: /^individual\.verification/,
    label: 'Your identity verification',
    hint: 'Upload a photo of a government-issued ID',
    group: true,
  },
  {
    match: /^individual\./,
    label: 'Personal information',
    group: true,
  },

  // ── Payout / bank ─────────────────────────────────────────────────
  {
    match: /^external_account$/,
    label: 'Bank account for payouts',
    hint: 'Add a bank account where Stripe should deposit your funds',
  },

  // ── Terms & Service ───────────────────────────────────────────────
  {
    match: /^tos_acceptance\./,
    label: 'Accept Stripe Terms of Service',
    group: true,
  },
];

/**
 * Translate a Stripe requirement array into a de-duplicated, human-labeled
 * checklist. Groups related codes into ONE row so the user sees
 * "Owner's date of birth" instead of three separate day/month/year lines.
 *
 * @param {string[]} codes - Stripe requirement codes
 * @returns {{label: string, hint?: string, codes: string[]}[]} — one entry
 *   per logical action the user needs to take, in a stable readable order.
 */
export function translateStripeRequirements(codes) {
  if (!Array.isArray(codes) || codes.length === 0) return [];
  const groups = new Map(); // label -> { label, hint, codes: [] }
  for (const raw of codes) {
    const code = typeof raw === 'string' ? raw : String(raw || '');
    if (!code) continue;
    const rule = _RULES.find((r) => r.match.test(code)) || {
      label: code,
      hint: undefined,
    };
    const key = rule.label;
    if (!groups.has(key)) {
      groups.set(key, { label: rule.label, hint: rule.hint, codes: [] });
    }
    groups.get(key).codes.push(code);
  }
  return Array.from(groups.values());
}

export default translateStripeRequirements;
